import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { projectsAPI } from '../lib/api';
import { useProject } from '../lib/project-context';
import { useProjects, useCreateProject, useDeleteProject } from '../lib/queries';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { useConfirm } from '../components/ui/confirm-dialog';
import { Plus, Search, FolderKanban, MapPin, TrendingUp, DollarSign, BarChart3, Building2, Copy, Archive, RotateCcw, Trash2, ArrowUpDown } from 'lucide-react';
import { STATUS_MAP, formatCurrency } from '../lib/utils';
import { getReturnMetric } from '../lib/returnMetric';
import { toast } from 'sonner';

const TYPE_LABELS = {
  residencial:'Residencial',residencial_plus:'Residencial Plus',medio_residencial:'Medio Residencial',
  interes_social:'Interés Social',mixto:'Mixto',comercial:'Comercial',industrial:'Industrial'
};

const STATE_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-slate-100 text-slate-600' },
  in_analysis: { label: 'En análisis', color: 'bg-blue-50 text-blue-600' },
  active: { label: 'Activo', color: 'bg-emerald-50 text-emerald-600' },
  en_planeacion: { label: 'En planeación', color: 'bg-amber-50 text-amber-600' },
  en_preventa: { label: 'En preventa', color: 'bg-cyan-50 text-cyan-600' },
  en_construccion: { label: 'En construcción', color: 'bg-orange-50 text-orange-600' },
  completado: { label: 'Completado', color: 'bg-emerald-50 text-emerald-700' },
  en_ventas: { label: 'En ventas', color: 'bg-purple-50 text-purple-600' },
  archived: { label: 'Archivado', color: 'bg-gray-100 text-gray-500' },
};

export default function ProjectsPage() {
  const { setActiveProject, refreshProjects: refreshCtx, activeProject } = useProject();
  const { data: projects = [], isLoading: loading } = useProjects();
  const createProjectMut = useCreateProject();
  const deleteProjectMut = useDeleteProject();
  const confirm = useConfirm();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [showCreate, setShowCreate] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', location: '', project_type: 'residencial', status: 'draft' });
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  const handleProjectClick = (project) => {
    setActiveProject(project);
    navigate(`/projects/${project.id}`);
  };

  const handleCreate = async () => {
    if (!newProject.name.trim()) return;
    setCreating(true);
    try {
      const data = await createProjectMut.mutateAsync(newProject);
      toast.success(`Proyecto "${data.name}" creado`);
      setShowCreate(false);
      setNewProject({ name: '', description: '', location: '', project_type: 'residencial', status: 'draft' });
      setActiveProject(data);
      refreshCtx();
      navigate(`/projects/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setCreating(false);
  };

  const handleDuplicate = async (e, id) => {
    e.stopPropagation();
    try { await projectsAPI.duplicate(id); toast.success('Proyecto duplicado'); refreshCtx(); } catch { toast.error('Error'); }
  };

  const handleArchive = async (e, id) => {
    e.stopPropagation();
    try { await projectsAPI.archive(id); toast.success('Proyecto archivado'); if (activeProject?.id === id) setActiveProject(null); refreshCtx(); } catch { toast.error('Error'); }
  };

  const handleRestore = async (e, id) => {
    e.stopPropagation();
    try { await projectsAPI.restore(id); toast.success('Proyecto restaurado'); refreshCtx(); } catch { toast.error('Error'); }
  };

  const handleDelete = async (e, id, name) => {
    e.stopPropagation();
    const ok = await confirm({
      title: `¿Eliminar "${name}"?`,
      description: 'Esta acción no se puede deshacer. Se eliminarán todos los escenarios y análisis asociados al proyecto.',
      confirmLabel: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deleteProjectMut.mutateAsync(id);
      toast.success('Eliminado');
      if (activeProject?.id === id) setActiveProject(null);
      refreshCtx();
    } catch (err) { console.error('[Projects] delete failed', err); toast.error('Error'); }
  };

  const filtered = useMemo(() => {
    let result = projects.filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.location || '').toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === 'all' || p.status === filterStatus;
      const matchType = filterType === 'all' || p.project_type === filterType;
      return matchSearch && matchStatus && matchType;
    });
    result.sort((a, b) => {
      if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
      if (sortBy === 'created') return (b.created_at || '').localeCompare(a.created_at || '');
      return (b.updated_at || '').localeCompare(a.updated_at || '');
    });
    return result;
  }, [projects, search, filterStatus, filterType, sortBy]);

  const counts = { total: projects.length, active: projects.filter(p => p.status !== 'archived').length, archived: projects.filter(p => p.status === 'archived').length };

  return (
    <div className="px-6 lg:px-8 py-6 max-w-[1440px]" data-testid="projects-page">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Proyectos</h1>
            <p className="text-sm text-muted-foreground mt-1">{counts.active} activos · {counts.archived} archivados</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="project-create-button" style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90"><Plus className="mr-2 h-4 w-4" /> Nuevo Proyecto</Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Nuevo proyecto</DialogTitle></DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2"><Label>Nombre del proyecto</Label><Input placeholder="Torre Reforma 250" value={newProject.name} onChange={(e) => setNewProject({...newProject, name: e.target.value})} data-testid="project-name-input" autoFocus /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Ubicación</Label><Input placeholder="Guadalajara, Jal." value={newProject.location} onChange={(e) => setNewProject({...newProject, location: e.target.value})} /></div>
                  <div className="space-y-2"><Label>Tipo</Label>
                    <Select value={newProject.project_type} onValueChange={(v) => setNewProject({...newProject, project_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2"><Label>Descripción</Label><Input placeholder="Descripción breve" value={newProject.description} onChange={(e) => setNewProject({...newProject, description: e.target.value})} /></div>
                <div className="space-y-2"><Label>Estado inicial</Label>
                  <Select value={newProject.status} onValueChange={(v) => setNewProject({...newProject, status: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_MAP).filter(([k]) => k !== 'archived').map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button><Button onClick={handleCreate} disabled={creating || !newProject.name.trim()} data-testid="project-submit-button">{creating ? 'Creando...' : 'Crear proyecto'}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters bar */}
        <Card className="rounded-xl border-border/50 shadow-sm mb-5">
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Buscar proyecto..." className="pl-9 h-9" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="projects-search-input" />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todos los estados</SelectItem>{Object.entries(STATE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Tipo" /></SelectTrigger>
                <SelectContent><SelectItem value="all">Todos los tipos</SelectItem>{Object.entries(TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px] h-9"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="updated">Última actualización</SelectItem><SelectItem value="created">Fecha de creación</SelectItem><SelectItem value="name">Nombre (A-Z)</SelectItem></SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground ml-auto">{filtered.length} proyectos</span>
            </div>
          </CardContent>
        </Card>

        {/* Project grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">{[1,2,3].map(i => <Skeleton key={i} className="h-44 rounded-xl" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FolderKanban className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--primary-hex)', opacity: 0.3 }} />
            <h3 className="font-display text-lg font-medium mb-2">{search || filterStatus !== 'all' || filterType !== 'all' ? 'Sin resultados' : 'Crea tu primer proyecto'}</h3>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">{search ? 'Intenta con otros filtros' : 'Modela la viabilidad financiera de tu desarrollo inmobiliario.'}</p>
            {!search && <Button onClick={() => setShowCreate(true)} style={{ background: 'var(--accent-primary)' }} className="text-white hover:opacity-90"><Plus className="mr-2 h-4 w-4" /> Crear proyecto</Button>}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((p, i) => {
              const state = STATE_CONFIG[p.status] || STATE_CONFIG.draft;
              const isArchived = p.status === 'archived';
              const rm = getReturnMetric(p.last_analysis?.metrics);
              const van = p.last_analysis?.metrics?.npv;
              return (
                <motion.div key={p.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, delay: i * 0.02 }}>
                  <Card className={`rounded-xl border-border/50 shadow-sm hover:shadow-md transition-[box-shadow] duration-200 cursor-pointer group ${isArchived ? 'opacity-60' : ''}`}
                    data-testid={`project-card-${p.id}`} onClick={() => handleProjectClick(p)}>
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-semibold text-base group-hover:text-[color:var(--primary-hex)] transition-[color] duration-150 truncate">{p.name}</h3>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            {p.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {p.location}</span>}
                            <span className="flex items-center gap-1"><Building2 className="h-3 w-3" /> {TYPE_LABELS[p.project_type] || p.project_type}</span>
                          </div>
                        </div>
                        <Badge variant="secondary" className={`text-[10px] shrink-0 ${state.color}`}>{state.label}</Badge>
                      </div>
                      {p.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-1">{p.description}</p>}
                      {rm.value != null ? (
                        <div className="flex items-center gap-4 pt-2 border-t border-border/50">
                          <div className="flex items-center gap-1.5" data-testid={`project-return-${p.id}`}><TrendingUp className="h-3.5 w-3.5" style={{ color: rm.value > 0 ? 'var(--success)' : 'var(--danger)' }} /><span className="text-sm font-display font-semibold tabular-nums">{rm.display}</span><span className="text-[10px] text-muted-foreground">{rm.label}</span></div>
                          {van != null && <div className="flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" style={{ color: van > 0 ? 'var(--success)' : 'var(--danger)' }} /><span className="text-sm font-display font-semibold tabular-nums">{formatCurrency(van)}</span><span className="text-[10px] text-muted-foreground">VAN inv.</span></div>}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 pt-2 border-t border-border/50 text-xs text-muted-foreground"><BarChart3 className="h-3 w-3" /> Sin análisis</div>
                      )}
                      {/* Quick actions on hover */}
                      <div className="flex items-center gap-1 mt-3 pt-2 border-t border-border/30 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                        <Button variant="ghost" size="icon" className="h-7 w-7" title="Duplicar" onClick={(e) => handleDuplicate(e, p.id)}><Copy className="h-3.5 w-3.5" /></Button>
                        {isArchived ? (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Restaurar" onClick={(e) => handleRestore(e, p.id)}><RotateCcw className="h-3.5 w-3.5" /></Button>
                        ) : (
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Archivar" onClick={(e) => handleArchive(e, p.id)}><Archive className="h-3.5 w-3.5" /></Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive ml-auto" title="Eliminar" onClick={(e) => handleDelete(e, p.id, p.name)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        <span className="text-[10px] text-muted-foreground tabular-nums">{p.updated_at ? new Date(p.updated_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : ''}</span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </motion.div>
    </div>
  );
}
